import { Injectable } from '@angular/core';
import { AbstractControl, UntypedFormArray, ValidationErrors, ValidatorFn } from '@angular/forms';
import { CountriesService } from '@core/services/countries.service';
import { StatusService } from '@core/services/status.service';
import { AutomationAPI } from '@core/typings/api/automation.typing';
import { Automation, FOUND_IN_CDT, NOT_FOUND_IN_CDT } from '@core/typings/ui/automation.typing';
import { ReferenceFieldsUI } from '@core/typings/ui/reference-fields.typing';
import { ClientSettingsService } from '@features/client-settings/client-settings.service';
import { FormDefinitionCompWithField } from '@features/configure-forms/form.typing';
import { CustomDataTablesService } from '@features/custom-data-tables/services/custom-data-table.service';
import { EmployeeSSOFieldsService } from '@features/employee-sso-fields/employee-sso-fields.service';
import { FormFieldAutomationService } from '@features/form-fields/services/form-field-automation.service';
import { FormFieldHelperService } from '@features/form-fields/services/form-field-helper.service';
import { ComponentHelperService } from '@features/forms/services/component-helper/component-helper.service';
import { NonprofitService } from '@features/nonprofit/nonprofit.service';
import { UserService } from '@features/users/user.service';
import { SimpleStringMap } from '@yourcause/common';
import { TypeSafeFormBuilder, TypeaheadSelectOption } from '@yourcause/common/core-forms';
import { DateService, TIMESTAMP_FORMAT } from '@yourcause/common/date';
import { I18nService } from '@yourcause/common/i18n';
import { ArrayHelpersService } from '@yourcause/common/utils';

@Injectable({ providedIn: 'root' })
export class RuleAutomationService {
  private formRuleObjectMap: SimpleStringMap<Automation.ObjectConfig> = {};

  constructor (
    private i18n: I18nService,
    private statusService: StatusService,
    private arrayHelper: ArrayHelpersService,
    private customDataTablesService: CustomDataTablesService,
    private userService: UserService,
    private componentHelper: ComponentHelperService,
    private countriesService: CountriesService,
    private employeeSSOFieldsService: EmployeeSSOFieldsService,
    private clientSettingsService: ClientSettingsService,
    private formBuilder: TypeSafeFormBuilder,
    private nonprofitService: NonprofitService,
    private formFieldHelperService: FormFieldHelperService,
    private formFieldAutomationService: FormFieldAutomationService,
    private dateService: DateService
  ) { }

  /**
   * Get Selected Options
   *
   * @returns the Selected options
   */
  getSelectedOptions () {
    return [{
      label: this.i18n.translate(
        'GLOBAL:lblIsSelected',
        {},
        'Is selected'
      ),
      value: true
    }, {
      label: this.i18n.translate(
        'GLOBAL:lblIsNotSelected',
        {},
        'Is not selected'
      ),
      value: false
    }];
  }

  /**
   * Get File Upload Options
   *
   * @returns the file upload options
   */
  getFileUploadOptions () {
    return [{
      label: this.i18n.translate(
        'GLOBAL:lblIsUploaded',
        {},
        'Is uploaded'
      ),
      value: true
    }, {
      label: this.i18n.translate(
        'GLOBAL:lblIsNotUploaded',
        {},
        'Is not uploaded'
      ),
      value: false
    }];
  }

  /**
   * Get Comparison Negate Options
   *
   * @returns the Comparison negate options
   */
  getComparisonNegateOptions () {
    return [{
      label: this.i18n.translate('GLOBAL:lblIs', {}, 'is'),
      value: false
    }, {
      label: this.i18n.translate('GLOBAL:lblIsNot', {}, 'is not'),
      value: true
    }, {
      label: this.i18n.translate(
        'common:textIsFoundInCdt',
        {},
        'is found in custom data table'
      ),
      value: FOUND_IN_CDT
    }, {
      label: this.i18n.translate(
        'common:textIsNotFoundInCdt',
        {},
        'is not found in custom data table'
      ),
      value: NOT_FOUND_IN_CDT
    }];
  }

  /**
   * Get Majority Options
   *
   * @returns the Majority options
   */
  getMajorityOptions () {
    return [{
      label: this.i18n.translate('common:lblAllCap', {}, 'All'),
      value: Automation.MajorityOptions.All
    }, {
      label: this.i18n.translate('GLOBAL:lblMajority', {}, 'Majority'),
      value: Automation.MajorityOptions.Majority
    }, {
      label: this.i18n.translate('GLOBAL:lblOne', {}, 'One'),
      value: Automation.MajorityOptions.One
    }];
  }

  /**
   * Get Date Options
   *
   * @returns the Date options
   */
  getDateOptions () {
    return [{
      label: this.i18n.translate('common:textIsBetween', {}, 'Is between'),
      value: Automation.CriteriaComparisonTypes.Default
    }, {
      label: this.i18n.translate('common:textToday', {}, 'Today'),
      value: Automation.CriteriaComparisonTypes.Today
    }, {
      label: this.i18n.translate('common:textYesterday', {}, 'Yesterday'),
      value: Automation.CriteriaComparisonTypes.Yesterday
    }, {
      label: this.i18n.translate('common:textTomorrow', {}, 'Tomorrow'),
      value: Automation.CriteriaComparisonTypes.Tomorrow
    }, {
      label: this.i18n.translate('common:textLastWeek', {}, 'Last week'),
      value: Automation.CriteriaComparisonTypes.LastWeek
    }, {
      label: this.i18n.translate('common:textThisWeek', {}, 'This week'),
      value: Automation.CriteriaComparisonTypes.ThisWeek
    }, {
      label: this.i18n.translate('common:textLastMonth', {}, 'Last month'),
      value: Automation.CriteriaComparisonTypes.LastMonth
    }, {
      label: this.i18n.translate('common:textThisMonth', {}, 'This month'),
      value: Automation.CriteriaComparisonTypes.ThisMonth
    }, {
      label: this.i18n.translate('common:textLastYear', {}, 'Last year'),
      value: Automation.CriteriaComparisonTypes.LastYear
    }, {
      label: this.i18n.translate('common:textThisYear', {}, 'This year'),
      value: Automation.CriteriaComparisonTypes.ThisYear
    }, {
      label: this.i18n.translate('common:textLast365Days', {}, 'Last 365 days'),
      value: Automation.CriteriaComparisonTypes.Last365Days
    }];
  }

  /**
   * Get Number Range Options
   *
   * @returns number range options
   */
  getNumberRangeOptions () {
    return [{
      label: this.i18n.translate('common:textIsBetween', {}, 'Is between').toLowerCase(),
      value: false
    }, {
      label: this.i18n.translate(
        'common:textIsFoundInCdt',
        {},
        'is found in custom data table'
      ),
      value: FOUND_IN_CDT
    }, {
      label: this.i18n.translate(
        'common:textIsNotFoundInCdt',
        {},
        'is not found in custom data table'
      ),
      value: NOT_FOUND_IN_CDT
    }];
  }

  /**
   * Get Number Is Options
   *
   * @returns the Number is options
   */
  getNumberIsOptions () {
    return [{
      label: this.i18n.translate('GLOBAL:lblEqualsLowercase', {}, 'equals'),
      value: false
    }];
  }

  /**
   * Get Application Object
   *
   * @param isPostApplicationCreate: is this after the app is created?
   * @param isRouting: is this for routing?
   * @returns the Application Object for Automation
   */
  getApplicationObject (
    isPostApplicationCreate: boolean,
    isRouting = false
  ): Automation.ObjectConfig {
    return {
      label: this.i18n.translate('common:lblApplication'),
      key: 'Application',
      columns: this.arrayHelper.sort([{
        label: this.i18n.translate(
          'GLOBAL:lblCashAmountRequested',
          {},
          'Cash amount requested'
        ),
        key: 'AmountRequested',
        removeObjectName: true,
        comparison: Automation.Comparisons.NumberRange
      }, !isRouting ? {
        label: this.i18n.translate(
          'GLOBAL:lblInKindAmountRequested',
          {},
          'In-kind amount requested'
        ),
        key: 'InKindAmountRequested',
        removeObjectName: true,
        comparison: Automation.Comparisons.NumberRange
      } : undefined, !isRouting ? {
        label: this.i18n.translate(
          'GLOBAL:lblTotalAmountRequested',
          {},
          'Total amount requested'
        ),
        key: 'TotalAmountRequested',
        removeObjectName: true,
        comparison: Automation.Comparisons.NumberRange
      } : undefined, isPostApplicationCreate ? {
        label: this.i18n.translate(
          'GLOBAL:textRecommendedFundingAmount',
          {},
          'Recommended funding amount'
        ),
        key: 'RecommendedFundingAmount',
        removeObjectName: true,
        comparison: Automation.Comparisons.NumberRange
      } : undefined,
      isPostApplicationCreate ? {
        label: this.i18n.translate(
          'common:hdrStatus',
          {},
          'Status'
        ),
        key: 'ApplicationStatus',
        removeObjectName: true,
        comparison: Automation.Comparisons.SelectIs,
        options: this.statusService.get('appStatusOptions')
      } : undefined].filter((item) => !!item) as any, 'label')
    };
  }

  /**
   * Get Applicant Object
   *
   * @returns the Applicant Object for automation
   */
  getApplicantObject (): Automation.ObjectConfig {
    return {
      label: this.i18n.translate(
        'common:lblApplicant'
      ),
      key: 'ApplicantInfo',
      columns: this.arrayHelper.sort([{
        label: this.i18n.translate(
          'GLOBAL:CITY'
        ),
        key: 'City',
        comparison: Automation.Comparisons.TextIs
      }, {
        label: this.i18n.translate(
          'GLOBAL:lblState'
        ),
        key: 'State',
        comparison: Automation.Comparisons.TextIs
      }, {
        label: this.i18n.translate(
          'GLOBAL:COUNTRY'
        ),
        key: 'Country',
        comparison: Automation.Comparisons.SelectIs,
        options: this.countriesService.countrySelects
      }, {
        label: this.i18n.translate(
          'GLOBAL:lblPostalCode'
        ),
        key: 'PostalCode',
        comparison: Automation.Comparisons.TextIs
      }], 'label')
    };
  }

  /**
   * Get Budget Object
   *
   * @returns the Budget Object for automation
   */
  getBudgetObject (): Automation.ObjectConfig {
    return {
      label: this.i18n.translate(
        'common:lblBudget'
      ),
      key: 'Budget',
      columns: this.arrayHelper.sort([{
        label: this.i18n.translate(
          'GLOBAL:textTotalRemaining',
          {},
          'Total remaining'
        ),
        key: 'TotalRemaining',
        comparison: Automation.Comparisons.NumberRange
      }], 'label')
    };
  }

  /**
   * Get Organization Object
   *
   * @param includeAggregates: Should the object include aggregate columns?
   * @returns the Organization Object for automation
   */
  getOrganizationObject (
    includeAggregates = true
  ): Automation.ObjectConfig {
    const standardCols: Automation.ObjectColumnConfig[] = this.arrayHelper.sort([{
      label: this.i18n.translate(
        'GLOBAL:CITY'
      ),
      key: 'City',
      comparison: Automation.Comparisons.TextIs
    }, {
      label: this.i18n.translate(
        'GLOBAL:lblState'
      ),
      key: 'State',
      comparison: Automation.Comparisons.TextIs
    }, {
      label: this.i18n.translate(
        'GLOBAL:COUNTRY'
      ),
      key: 'Country',
      comparison: Automation.Comparisons.SelectIs,
      options: this.countriesService.countrySelects
    }, {
      label: this.i18n.translate(
        'GLOBAL:lblPostalCode'
      ),
      key: 'PostalCode',
      comparison: Automation.Comparisons.TextIs
    }, {
      label: this.i18n.translate(
        'common:textBbgfEligible',
        {},
        'BBGF eligible'
      ),
      key: 'BBGFEligible',
      comparison: Automation.Comparisons.IsSelected,
      options: [{
        label: this.i18n.translate(
          'common:textIsTrue',
          {},
          'Is true'
        ),
        value: true
      }, {
        label: this.i18n.translate(
          'common:textIsFalse',
          {},
          'Is false'
        ),
        value: false
      }]
    }, {
      label: this.i18n.translate(
        'nonprofit:textSubsectionCode',
        {},
        'Subsection code'
      ),
      key: 'SubsectionCode',
      comparison: Automation.Comparisons.SelectIs,
      options: this.nonprofitService.getSubsectionCodeOptions()
    }], 'label');
    const config: Automation.ObjectConfig = {
      label: this.i18n.translate(
        'common:lblOrganization'
      ),
      key: 'OrganizationInfo',
      columns: standardCols
    };
    if (includeAggregates) {
      const aggregateCols: Automation.ObjectColumnConfig[] = [{
        label: this.i18n.translate(
          'nonprofit:textTotalAwardedYearToDate',
          {},
          'Total Awarded - year to date'
        ),
        key: 'aggregatedTotalAwardedAmount',
        comparison: Automation.Comparisons.NumberRange
      }, {
        label: this.i18n.translate(
          'nonprofit:textTotalRequestedYearToDate',
          {},
          'Total Requested - year to date'
        ),
        key: 'aggregateTotalRequestedAmount',
        comparison: Automation.Comparisons.NumberRange
      }, {
        label: this.i18n.translate(
          'nonprofit:textTotalRequestedPerProgramForInProgressYTD',
          {},
          'Total Requested per program for In Progress - Year to date'
          ),
        key: 'aggregateTotalRequestedAmountInProgress',
        comparison: Automation.Comparisons.NumberRange
      }, {
        label: this.i18n.translate(
          'nonprofit:textTotalRequestedPerProgramForAwaitingReviewYTD',
          {},
          'Total Requested per program for Awaiting Review - Year to date'
          ),
        key: 'aggregateTotalRequestedAmountAwaitingReview',
        comparison: Automation.Comparisons.NumberRange
      }, {
        label: this.i18n.translate(
          'nonprofit:textTotalRequestedPerProgramForApprovedYTD',
          {},
          'Total Requested per program for Approved - Year to date'
          ),
        key: 'aggregateTotalRequestedAmountApproved',
        comparison: Automation.Comparisons.NumberRange
      }, {
        label: this.i18n.translate(
          'nonprofit:textTotalRequestedPerProgramForHoldYTD',
          {},
          'Total Requested per program for Hold - Year to date'
          ),
        key: 'aggregateTotalRequestedAmountHold',
        comparison: Automation.Comparisons.NumberRange
      }, {
        label: this.i18n.translate(
          'nonprofit:textTotalRequestedPerProgramForCancelledYTD',
          {},
          'Total Requested per program for Cancelled - Year to date'
          ),
        key: 'aggregateTotalRequestedAmountCancelled',
        comparison: Automation.Comparisons.NumberRange
      }, {
        label: this.i18n.translate(
          'nonprofit:textTotalRequestedPerProgramForDeclinedYTD',
          {},
          'Total Requested per program for Declined - Year to date'
          ),
        key: 'aggregateTotalRequestedAmountDeclined',
        comparison: Automation.Comparisons.NumberRange
      }, {
        label: this.i18n.translate(
          'nonprofit:textTotalRequestedPerProgramForArchivedYTD',
          {},
          'Total Requested per program for Archived - Year to date'
          ),
        key: 'aggregateTotalRequestedAmountIsArchived',
        comparison: Automation.Comparisons.NumberRange
      }, {
        label: this.i18n.translate(
          'nonprofit:textTotalRequestedPerProgramForDraftYTD',
          {},
          'Total Requested per program for Draft - Year to date'
          ),
        key: 'aggregateTotalRequestedAmountIsDraft',
        comparison: Automation.Comparisons.NumberRange
      }, {
        label: this.i18n.translate(
          'nonprofit:textTotalAwardedPerProgramForApprovedYTD',
          {},
          'Total Awarded per program for Approved - Year to date'
          ),
        key: 'aggregatedTotalAwardedAmountApproved',
        comparison: Automation.Comparisons.NumberRange
      }, {
        label: this.i18n.translate(
          'nonprofit:textTotalAmountRecommendedFundingApprovedNotAwardedYTD',
          {},
          'Total Recommended Funding for Approved but not Awarded per program - Year to date'
          ),
        key: 'aggregatedAmountRecommendedFundingApprovedNotAwarded',
        comparison: Automation.Comparisons.NumberRange
      }];
      config.columns = this.arrayHelper.sort([
        ...standardCols,
        ...aggregateCols
      ], 'label');
    }

    return config;
  }

  /**
   * Get Employee SSO Object
   *
   * @returns the Employee SSO Object for automation
   */
  getEmployeeSsoObject (): Automation.ObjectConfig {
    if (this.clientSettingsService.clientSettings.hasSSO) {
      this.employeeSSOFieldsService.setEmployeeColumnsForAutomation();

      return {
        label: this.i18n.translate(
          'GLOBAL:lblEmployee'
        ),
        key: 'EmployeeApplicantInfo',
        columns: this.arrayHelper.sort(this.employeeSSOFieldsService.get(
          'employeeColumnsForAutomation'
        ), 'label')
      };
    }

    return null;
  }

  /**
   * Get Aggregate Object
   *
   * @returns the Aggregate Object for automation
   */
  getAggregateObject (): Automation.ObjectConfig {
    const aggregateFields = this.formFieldHelperService.getAllAggregateFields();
    if (aggregateFields.length > 0) {
      return {
        label: this.i18n.translate(
          'GLOBAL:textAggregate',
          {},
          'Aggregate'
        ),
        key: 'referenceFields',
        columns: this.arrayHelper.sort(aggregateFields.map((field) => {
          return {
            label: field.name,
            key: field.key,
            comparison: Automation.Comparisons.NumberRange
          };
        }), 'label')
      };
    }

    return null;
  }

  /**
   * Get Award Object
   *
   * @returns the Award Object for automation
   */
  getAwardObject (): Automation.ObjectConfig {
    return {
      label: this.i18n.translate('common:lblAward'),
      key: 'Award',
      columns: this.arrayHelper.sort([{
        label: this.i18n.translate(
          'GLOBAL:lblTotalAwardAmount',
          {},
          'Total award amount'
        ),
        key: 'TotalAwardAmount',
        comparison: Automation.Comparisons.NumberRange
      }, {
        label: this.i18n.translate(
          'GLOBAL:lblLastAwardDate',
          {},
          'Last award date'
        ),
        key: 'LastAwardDateModel',
        comparison: Automation.Comparisons.DateRange
      }], 'label')
    };
  }

  /**
   * Get Decision Object
   *
   * @returns the Decision Object for automation
   */
  getDecisionObject (): Automation.ObjectConfig {
    return {
      label: this.i18n.translate(
        'GLOBAL:lblDecision'
      ),
      key: 'Decisions',
      columns: this.arrayHelper.sort([{
        label:  this.i18n.translate(
          'GLOBAL:lblApprovalRatio',
          {},
          'Approval ratio'
        ),
        key: 'DecisionsYesRatio',
        comparison: Automation.Comparisons.Majority
      }, {
        label:  this.i18n.translate(
          'GLOBAL:lblDeclinationRatio',
          {},
          'Declination ratio'
        ),
        key: 'DecisionsNoRatio',
        comparison: Automation.Comparisons.Majority
      }, {
        label:  this.i18n.translate(
          'GLOBAL:lblApprovalCount',
          {},
          'Approval count'
        ),
        key: 'DecisionsYesCount',
        comparison: Automation.Comparisons.NumberIs
      }, {
        label:  this.i18n.translate(
          'GLOBAL:lblDeclinationCount',
          {},
          'Declination count'
        ),
        key: 'DecisionsNoCount',
        comparison: Automation.Comparisons.NumberIs
      }], 'label')
    };
  }

  /**
   * Get Payment Object
   *
   * @returns the Payment Object for automation
   */
  getPaymentObject (): Automation.ObjectConfig {
    return {
      label: this.i18n.translate('common:lblPayment'),
      key: 'Payment',
      columns: this.arrayHelper.sort([{
        label: this.i18n.translate(
          'GLOBAL:lblHasPayment',
          {},
          'Has payment'
        ),
        key: 'HasPayment',
        comparison: Automation.Comparisons.IsSelected,
        options: [{
          label: this.i18n.translate(
            'common:textIsTrue',
            {},
            'Is true'
          ),
          value: true
        }, {
          label: this.i18n.translate(
            'common:textIsFalse',
            {},
            'Is false'
          ),
          value: false
        }]
      }, {
        label: this.i18n.translate(
          'GLOBAL:lblDisbursedPaymentRatio',
          {},
          'Disbursed payment ratio'
        ),
        key: 'DisbursedPaymentRatio',
        comparison: Automation.Comparisons.Majority
      }, {
        label: this.i18n.translate(
          'GLOBAL:lblPaymentAmountEqualsAwardAmount',
          {},
          'Payment amount equals award amount'
        ),
        key: 'PaymentAmountMatchesAwardAmount',
        comparison: Automation.Comparisons.IsSelected,
        options: [{
          label: this.i18n.translate(
            'common:textIsTrue',
            {},
            'Is true'
          ),
          value: true
        }, {
          label: this.i18n.translate(
            'common:textIsFalse',
            {},
            'Is false'
          ),
          value: false
        }]
      }], 'label')
    };
  }

  /**
   * Takes a form and converts it to the Object Config
   *
   * @param form: Form Object
   * @returns Object Configuration
   */
  async formToRuleObject (
    form: Automation.FormForRuleObject
  ): Promise<Automation.ObjectConfig> {
    if (this.formRuleObjectMap[form.id]) {
      return this.formRuleObjectMap[form.id];
    }
    const comps: FormDefinitionCompWithField[] = [];
    form.formDefinition.forEach((tab) => {
      this.componentHelper.eachComponent(tab.components, (comp) => {
        if (this.componentHelper.isReferenceFieldComp(comp.type)) {
          const refFieldOriginalKey = this.componentHelper.getRefFieldKeyFromCompType(comp.type);
          const refField = this.formFieldHelperService.getReferenceFieldByKey(refFieldOriginalKey);
          const supportedMultiType = [
            ReferenceFieldsUI.ReferenceFieldTypes.SelectBoxes,
            ReferenceFieldsUI.ReferenceFieldTypes.CustomDataTable,
            ReferenceFieldsUI.ReferenceFieldTypes.PicklistGroup
          ].includes(refField.type);
          if (
            (!refField.supportsMultiple || supportedMultiType) &&
            !refField.isTableField &&
            !refField.isRichText &&
            refField.type !== ReferenceFieldsUI.ReferenceFieldTypes.Table &&
            refField.type !== ReferenceFieldsUI.ReferenceFieldTypes.Address &&
            refField.isSingleResponse &&
            !refField.isEncrypted
          ) {
            comps.push({
              ...comp,
              field: refField
            });
          }
        }
      });
    });
    const columns: Automation.ObjectColumnConfig[] = [];
    const guids = comps.map((comp) => {
      return comp.field.customDataTableGuid;
    }).filter((guid) => !!guid);
    await this.customDataTablesService.setCdtOptionsInBulkForFormIds(
      guids,
      this.userService.getCurrentUserCulture(),
      [form.id]
    );
    comps.forEach((comp) => {
      if (this.componentHelper.isReferenceFieldComp(comp.type)) {
        const newColumn = this.formFieldAutomationService.refFieldTypeToAutomation(comp.label, comp.field);
        columns.push(newColumn);
      }
    });

    return this.formRuleObjectMap[form.id] = {
      label: form.name,
      key: 'referenceFields',
      columns: this.arrayHelper.sort(columns, 'label')
    };
  }

  /**
   * Clears the Form Rule Object Map
   */
  clearFormRuleObjectMap () {
    this.formRuleObjectMap = {};
  }

  isCdtTypeRule (negated: Automation.NegatedType) {
    return negated === FOUND_IN_CDT || negated === NOT_FOUND_IN_CDT;
  }

  /**
   * Gets the adapted rules for the API
   *
   * @param rules: Rules to Adapt
   * @returns the adapted rules for the API
   */
  getAdaptedRulesForAPI (rules: Automation.CriteriaFormState[]) {
    return rules.reduce((acc, rule) => {
      return [
        ...acc,
        ...this.mapRuleForApi(rule, false)
      ];
    }, [] as AutomationAPI.SaveAutomationRuleSetExpressionModel[]);
  }

  /**
   * Adapt the UI rule for the API
   *
   * @param rule: Rule to adapt for API
   * @param applyFilters: Are we applying filters?
   * @returns the adapted rule for the API
   */
  mapRuleForApi (
    rule: Automation.CriteriaFormState,
    applyFilters = true
  ): AutomationAPI.SaveAutomationRuleSetExpressionModel[] {
    const models = this.adaptRuleForApi(rule);

    if (applyFilters) {
      return models.filter(model => !model.isRemoved ||
        model.automationRuleSetExpressionId);
    }

    return models;
  }

  /**
   * Adapts the rule for the API
   *
   * @param rule: Rule to Map
   * @returns the rule mapped to send to the API
   */
  adaptRuleForApi (
    rule: Automation.CriteriaFormState
  ): AutomationAPI.SaveAutomationRuleSetExpressionModel[] {
    const baseValue = rule.values.find(value => !value.isRemoved) ||
      rule.values[0];

    const negated = baseValue?.comparisonNegated;
    const isRemoved = baseValue?.isRemoved;
    const isCdtComparison = this.isCdtTypeRule(negated);

    if (rule.column) {
      const key = (rule.column.removeObjectName ? '' : rule.object?.key + '.') + rule.column.key;
      let referenceFieldId: number = null;
      if (rule.object?.key === 'referenceFields') {
        referenceFieldId = this.formFieldHelperService.referenceFieldMap[
          rule.column.key
        ]?.referenceFieldId ?? null;
      }
      switch (rule.column.comparison) {
        case Automation.Comparisons.DateRange:
          if (baseValue.comparisonType !== Automation.CriteriaComparisonTypes.Default) {
            // typed as any below because typescript doesn't know the values are the same (ensured by if check above)
            const baseComparisonType: AutomationAPI.AutomationRuleComparisonTypes = baseValue.comparisonType as any;

            return [{
              automationRuleSetExpressionId: baseValue.valueId,
              isRemoved,
              columnName: key,
              comparisonType: baseComparisonType,
              comparisonValue: '',
              referenceFieldId
            },
            // if we switched from between to something else, remove the between rules
            ...(baseValue.greaterThanId ? [{
              automationRuleSetExpressionId: baseValue.greaterThanId,
              isRemoved: true,
              columnName: key,
              comparisonType: baseComparisonType,
              comparisonValue: '',
              referenceFieldId
            }] : []),
            ...(baseValue.lessThanId ? [{
              automationRuleSetExpressionId: baseValue.lessThanId,
              isRemoved: true,
              columnName: key,
              comparisonType: baseComparisonType,
              comparisonValue: '',
              referenceFieldId
            }] : [])];
          }
          // fall through to number range since logic is the same
        // eslint-disable-next-line no-fallthrough
        case Automation.Comparisons.NumberRange:
          const isDate = rule.column.comparison === Automation.Comparisons.DateRange;

          return rule.values.reduce((acc, value) => {
            const { comparisonGreaterThan, comparisonLessThan } = value;
            let greaterThanValue = value.comparisonGreaterThan;
            let lessThanValue = value.comparisonLessThan;
            if (isDate) {
              if (!!greaterThanValue) {
                greaterThanValue = this.dateService.getStartOrEndOfDayInUtcFormatted(
                  new Date(greaterThanValue).toString(),
                  true,
                  TIMESTAMP_FORMAT
                );
              }
              if (!!lessThanValue) {
                lessThanValue = this.dateService.getStartOrEndOfDayInUtcFormatted(
                  new Date(lessThanValue).toString(),
                  false,
                  TIMESTAMP_FORMAT
                );
              }
            } else {
              greaterThanValue = '' + greaterThanValue;
              lessThanValue = '' + lessThanValue;
            }
            const comparisonGreaterThanObj = comparisonGreaterThan || (comparisonGreaterThan === 0) ?
              {
                automationRuleSetExpressionId: value.greaterThanId,
                isRemoved: value.isRemoved,
                columnName: key,
                comparisonType: AutomationAPI.AutomationRuleComparisonTypes.GreaterThanOrEquals,
                comparisonValue: greaterThanValue,
                referenceFieldId
              } :
              null;
            const comparisonLessThanObj = comparisonLessThan || comparisonLessThan === 0 ?
              {
                automationRuleSetExpressionId: value.lessThanId,
                isRemoved: value.isRemoved,
                columnName: key,
                comparisonType: AutomationAPI.AutomationRuleComparisonTypes.LessThanOrEquals,
                comparisonValue: lessThanValue,
                referenceFieldId
              } :
              null;
            if (!isCdtComparison) {
              // Standard number range does not support "valueId", so we mark it as removed
              const valueToRemove = value.valueId ?
                {
                  automationRuleSetExpressionId: value.valueId,
                  isRemoved: true,
                  columnName: key,
                  comparisonType: value.comparisonType,
                  comparisonValue: '',
                  referenceFieldId
                } :
              null;

              return [
                ...acc,
                valueToRemove,
                comparisonGreaterThanObj,
                comparisonLessThanObj
              ].filter(val => val !== null);
            } else {
              // CDT does not support greater than or less than so mark it as removed
              const greaterThanObjToRemove = value.greaterThanId ?
                {
                  ...comparisonGreaterThanObj,
                  isRemoved: true
                } :
                null;
              const lessThanObjToRemove = value.lessThanId ?
                {
                  ...comparisonLessThanObj,
                  isRemoved: true
                } :
                null;
              const valuesToRemove = [
                greaterThanObjToRemove,
                lessThanObjToRemove
              ].filter((item) => !!item);
              const cdtComparisonObj = {
                automationRuleSetExpressionId: value.valueId,
                isRemoved: value.isRemoved,
                columnName: key,
                comparisonType: this.getComparisonTypeForApi(negated),
                comparisonValue: '' + value.comparisonValue,
                referenceFieldId
              };

              return [
                ...acc,
                ...valuesToRemove,
                cdtComparisonObj
              ];
            }
          }, []);
        case Automation.Comparisons.SelectIs:
        case Automation.Comparisons.TextIs:
        case Automation.Comparisons.NumberIs:
          return rule.values.map(val => ({
            automationRuleSetExpressionId: val.valueId,
            isRemoved: val.isRemoved,
            columnName: key,
            comparisonType: this.getComparisonTypeForApi(negated),
            comparisonValue: '' + val.comparisonValue,
            referenceFieldId
          }));
        case Automation.Comparisons.IsUploaded:
          return [{
            automationRuleSetExpressionId: baseValue.valueId,
            isRemoved,
            columnName: key,
            comparisonType: baseValue.comparisonValue ?
              AutomationAPI.AutomationRuleComparisonTypes.HasAValue :
              AutomationAPI.AutomationRuleComparisonTypes.DoesNotHaveAValue,
            comparisonValue: '' + baseValue.comparisonValue,
            referenceFieldId
          }];
        case Automation.Comparisons.IsSelected:
          return [{
            automationRuleSetExpressionId: baseValue.valueId,
            isRemoved,
            columnName: key,
            comparisonType: AutomationAPI.AutomationRuleComparisonTypes.Equals,
            comparisonValue: '' + baseValue.comparisonValue,
            referenceFieldId
          }];
        case Automation.Comparisons.Majority:
          const comparisonValue = '' + (+baseValue.comparisonValue === Automation.MajorityOptions.All ? 1 :
            +baseValue.comparisonValue === Automation.MajorityOptions.Majority ? .5 : 0);
          const comparisonType = +baseValue.comparisonValue === Automation.MajorityOptions.All ?
            AutomationAPI.AutomationRuleComparisonTypes.Equals :
            AutomationAPI.AutomationRuleComparisonTypes.GreaterThan;

          return [{
            automationRuleSetExpressionId: baseValue.valueId,
            isRemoved,
            columnName: key,
            comparisonType,
            comparisonValue,
            referenceFieldId
          }];
      }
    }

    return [{
      isRemoved: true,
      automationRuleSetExpressionId: baseValue?.valueId ||
        baseValue?.greaterThanId ||
        baseValue?.lessThanId,
      columnName: null,
      comparisonType: null,
      comparisonValue: null,
      referenceFieldId: null
    }];
  }

  /**
   * Gets the API comparison based on the negated value
   *
   * @param negated: Negated Value
   * @returns The API Comparison
   */
  getComparisonTypeForApi (negated: Automation.NegatedType) {
    if (negated === FOUND_IN_CDT) {
      return AutomationAPI.AutomationRuleComparisonTypes.InCdt;
    } else if (negated === NOT_FOUND_IN_CDT) {
      return AutomationAPI.AutomationRuleComparisonTypes.NotInCdt;
    }

    return negated === true ?
      AutomationAPI.AutomationRuleComparisonTypes.NotEquals :
      AutomationAPI.AutomationRuleComparisonTypes.Equals;
  }

  /**
   * Gets the negated value from the API Comparison Type
   *
   * @param comparisonType: Comparison Type
   * @returns the negated value
   */
  getComparisonNegatedFromApi (
    comparisonType: AutomationAPI.AutomationRuleComparisonTypes
  ): Automation.NegatedType {
    switch (comparisonType) {
      case  AutomationAPI.AutomationRuleComparisonTypes.InCdt:
        return FOUND_IN_CDT;
      case  AutomationAPI.AutomationRuleComparisonTypes.NotInCdt:
        return NOT_FOUND_IN_CDT;
      default:
        return comparisonType === AutomationAPI.AutomationRuleComparisonTypes.NotEquals;

    }
  }

  /**
   * Gets the Validator for the Rules
   *
   * @returns the Rule Array Validator
   */
  ruleArrayValidator () {
    return (control: AbstractControl) => {
      const formArray = control as UntypedFormArray;
      if (formArray.length === 0) {
        return {};
      }
      for (let index = 0; index < formArray.length; index++) {
        const element = formArray.at(index);

        if (!element.value.isRemoved) {
          return null;
        }
      }

      return {};
    };
  }

  /**
   * Gets the Validator for a Rules Values
   *
   * @returns the Rule Value Array Validator
   */
  ruleValueArrayValidator (): ValidatorFn {
    return (control: AbstractControl) => {
      const formArray = control as UntypedFormArray;
      if (!formArray.parent) {
        return null;
      }
      const column = (<Automation.CriteriaFormState>formArray.parent.value).column;

      if (!column) {
        return null;
      }

      const baseFormGroup = formArray.at(0);
      if (!baseFormGroup) {
        return null;
      }
      for (let index = 0; index < formArray.length; index++) {
        const formGroup = formArray.at(index);
        const value: Automation.CriteriaValueFormState = formGroup.value;
        const isCdtComparison = this.isCdtTypeRule(value.comparisonNegated);
        const noComparisonValue = !value.comparisonValue &&
          value.comparisonValue !== false;

        if (value.isRemoved) {
          continue;
        }
        const requiredError = {
          required: true
        };
        switch (column.comparison) {
          case Automation.Comparisons.DateRange:
            if (value.comparisonType === Automation.CriteriaComparisonTypes.Default) {
              // Standard Date Range Requires Greater than and Less Than
              const baseErrors: ValidationErrors = {
                comparisonGreaterThan: !value.comparisonGreaterThan ? requiredError : null,
                comparisonLessThan: !value.comparisonLessThan ? requiredError : null,
                comparisonType: null
              };
              if (
                !!value.comparisonGreaterThan &&
                value.comparisonGreaterThan > value.comparisonLessThan
              ) {
                baseFormGroup.setErrors({
                  ...baseErrors,
                  comparisonGreaterThan: {
                    endAfterStart: {
                      i18nKey: 'common:textPleaseEnterEndDateAfterStart',
                      defaultValue: 'End date must be after start date'
                    }
                  }
                });
              } else {
                if (
                  !baseErrors.comparisonGreaterThan &&
                  !baseErrors.comparisonLessThan &&
                  !baseErrors.comparisonType
                ) {
                  baseFormGroup.setErrors(null);
                } else {
                  baseFormGroup.setErrors(baseErrors);
                }
              }
            } else {
              // Non standard date range requires a comparison type (ex. "This month")
              if (!value.comparisonType) {
                baseFormGroup.setErrors({
                  comparisonGreaterThan: null,
                  comparisonLessThan: null,
                  comparisonType: requiredError
                });
              } else {
                baseFormGroup.setErrors(null);
              }
            }
            break;
          // eslint-disable-next-line no-fallthrough
          case Automation.Comparisons.NumberRange:
            if (
              !isCdtComparison &&
              value.comparisonGreaterThan === null &&
              value.comparisonLessThan === null
            ) {
              baseFormGroup.setErrors({
                comparisonLessThan: {
                  required: true
                },
                comparisonGreaterThan: {
                  required: true
                }
              });
            } else if (isCdtComparison && noComparisonValue) {
              baseFormGroup.setErrors({
                comparisonValue: {
                  required: true
                }
              });
            } else {
              baseFormGroup.setErrors(null);
            }
            break;
          default:
            if (noComparisonValue) {
              formGroup.setErrors({
                comparisonValue: {
                  required: true
                }
              });
            } else {
              formGroup.setErrors(null);
            }
          }
      }

      return null as ValidationErrors;
    };
  }

  /**
   * Get the Column Comparison Options
   *
   * @param column: the column of the rule
   * @returns the column comparison options
   */
  getColumnComparisonOptions (column: Automation.ObjectColumnConfig) {
    return 'options' in column ?
      column.options :
      this.getSelectedOptions();
  }

  /**
   * Gets the sentence connector when creating a rule summary
   *
   * @param rule: the rule to evaluate
   * @returns the sentence connector for summary
   */
  getSentenceConnector (rule: Automation.CriteriaFormState) {
    let options: TypeaheadSelectOption[] = [];
    let findLabelFromOptions = false;
    let fallbackConnecterWord = '';
    const nonRemovedRules = rule.values.filter((val) => !val.isRemoved);
    const firstValue = nonRemovedRules[0];
    const isCdtRule = this.isCdtTypeRule(firstValue?.comparisonNegated);

    switch (rule.column.comparison) {
      case Automation.Comparisons.DateRange:
        const comparisonType = firstValue.comparisonType;
        const isBetween = comparisonType === Automation.CriteriaComparisonTypes.Default;
        if (!isBetween) {
          fallbackConnecterWord = this.i18n.translate('GLOBAL:lblIs', {}, 'is');
        }
        break;
      case Automation.Comparisons.NumberIs:
        options = this.getNumberIsOptions();
        findLabelFromOptions = true;
        break;
      case Automation.Comparisons.Majority:
        fallbackConnecterWord = this.i18n.translate('GLOBAL:lblIs', {}, 'is');
        break;
      case Automation.Comparisons.SelectIs:
      case Automation.Comparisons.TextIs:
        options = this.getComparisonNegateOptions();
        findLabelFromOptions = true;
        break;
      case Automation.Comparisons.NumberRange:
        if (isCdtRule) {
          options = this.getComparisonNegateOptions();
          findLabelFromOptions = true;
        }
        break;
    }
    if (findLabelFromOptions) {
      const found = options.find((opt) => {
        return opt.value === firstValue.comparisonNegated;
      });

      return found?.label?.toLowerCase() || '';
    } else {
      return fallbackConnecterWord?.toLowerCase() || '';
    }
  }

  /**
   * Gets the value connector when creating a rule summary
   *
   * @param rule: the rule to evaluate
   * @returns the value connector for the summary
   */
  getValueConnector (rule: Automation.CriteriaFormState) {
    const filteredValues = rule.values.filter((val) => !val.isRemoved);
    const negated = filteredValues[0]?.comparisonNegated;
    const isCdtRule = this.isCdtTypeRule(negated);

    return filteredValues.reduce((acc, value) => {
      let options: TypeaheadSelectOption[] = [];
      let attr: 'comparisonType'|'comparisonValue';
      let label = '';
      if (isCdtRule) {
        options = this.customDataTablesService.pickListIdTypeaheadOptions;
        attr = 'comparisonValue';
      } else {
        switch (rule.column.comparison) {
          case Automation.Comparisons.DateRange:
            const comparisonType = value.comparisonType;
            const isBetween = comparisonType === Automation.CriteriaComparisonTypes.Default;
            if (isBetween) {
              let date1 = null;
              let date2 = null;
              if (value.comparisonGreaterThan) {
                date1 = this.dateService.formatDate(value.comparisonGreaterThan);
              }
              if (value.comparisonLessThan) {
                date2 = this.dateService.formatDate(value.comparisonLessThan);
              }
              if (date1 && date2) {
                label = this.i18n.translate(
                  'common:textIsBetweenDynamic',
                  {
                    number1: date1,
                    number2: date2
                  },
                  'is between __number1__ and __number2__'
                );
              } else if (date1) {
                label = this.i18n.translate(
                  'common:textIsAfterDate',
                  {
                    date: date1
                  },
                  'is after __date__'
                );
              } else if (date2) {
                label = this.i18n.translate(
                  'common:textIsBeforeDate',
                  {
                    date: date2
                  },
                  'is before __date__'
                );
              }
            } else {
              options = this.getDateOptions();
              attr = 'comparisonType';
            }
            break;
          case Automation.Comparisons.IsSelected:
            options = this.getSelectedOptions();
            attr = 'comparisonValue';
            break;
          case Automation.Comparisons.IsUploaded:
            options = this.getFileUploadOptions();
            attr = 'comparisonValue';
            break;
          case Automation.Comparisons.Majority:
            options = this.getMajorityOptions();
            attr = 'comparisonValue';
            break;
          case Automation.Comparisons.SelectIs:
            options = this.getColumnComparisonOptions(rule.column);
            attr = 'comparisonValue';
            break;
          case Automation.Comparisons.NumberRange:
            const num1 = value.comparisonGreaterThan;
            const num2 = value.comparisonLessThan;
            const has1 = !!num1 || num1 === 0;
            const has2 = !!num2 || num2 === 0;
            if (has1 && has2) {
              label = this.i18n.translate(
                'common:textIsBetweenDynamic',
                {
                  number1: num1,
                  number2: num2
                },
                'is between __number1__ and __number2__'
              );
            } else if (has1) {
              label = this.i18n.translate(
                'common:textIsGreaterThanNumber',
                {
                  number: num1
                },
                'is greater than __number__'
              );
            } else if (has2) {
              label = this.i18n.translate(
                'common:textIsLessThanNumber',
                {
                  number: num2
                },
                'is less than __number__'
              );
            }
            break;
          case Automation.Comparisons.TextIs:
            label = value.comparisonValue as string;
            break;
          case Automation.Comparisons.NumberIs:
            label = value.comparisonValue as string;
            break;
        }
      }

      if (attr) {
        label = this.getLabelFromOptionsAndAttr(options, value, attr);
      }

      if (acc && label) {
        const hasMany = rule.column.comparison === Automation.Comparisons.TextIs ||
          rule.column.comparison === Automation.Comparisons.SelectIs;
        const showAnd = (negated === true) || (negated === NOT_FOUND_IN_CDT);
        const negatedConnector = hasMany ? ` ${this.i18n.translate(
          showAnd ? 'GLOBAL:lblAND' : 'GLOBAL:lblOR',
          {},
          showAnd ? 'AND' : 'OR'
        ).toLowerCase()}` : ',';

        return `${acc}${negatedConnector} ${label}`;
      } else if (acc) {
        return acc;
      } else {
        return label;
      }
    }, '');
  }

  /**
   * Get the Label by passing in the options and attr to use
   *
   * @param options: typeahead select options
   * @param value: the value
   * @param attr: the attribute to check for the value
   * @returns the label
   */
  getLabelFromOptionsAndAttr (
    options: TypeaheadSelectOption[],
    value: Automation.CriteriaValueFormState,
    attr: keyof Automation.CriteriaValueFormState
  ) {
    return options.find((opt) => {
      // eslint-disable-next-line eqeqeq
      return opt.value == value[attr];
    })?.label ?? '';
  }

  /**
   * Create a rule sentence based on a rule
   *
   * @param rule: the rule to evaluate
   * @returns a sentence summary of the rule
   */
  createRuleSentence (
    rule: Automation.CriteriaFormState
  ) {
    const column = rule.column;
    let sentence = '';
    if (column) {
      const objectLabel = rule.object.label;
      sentence = `${objectLabel} ${column.label.toLowerCase()}`;
      const sentenceConnector = this.getSentenceConnector(rule);
      if (sentenceConnector) {
        sentence = `${sentence} ${sentenceConnector}`;
      }
      const valueConnector = this.getValueConnector(rule);
      if (valueConnector) {
        sentence = `${sentence} ${valueConnector}`;
      }
    }

    return sentence;
  }

  /**
   * Map the rules from the API to display in drilldown
   *
   * @param apiRules: Rules to map
   * @param objects: Objects available
   * @returns the mapped API rules for display
   */
  mapAPIRulesToViewForDrilldown (
    apiRules: AutomationAPI.AutomationRuleSetExpressionBaseModel[],
    objects: Automation.ObjectConfig[]
  ): Automation.DisplayRule[] {
    const uiRules = this.mapAPIRulesToView(apiRules, objects);
    const errorRule = uiRules.find(rule => !!rule.err);
    if (errorRule) {
      throw errorRule.err;
    }
    const rules = uiRules.map((uiRule) => {
      const column = uiRule.column;
      const comparisonNegated = uiRule.values[0]?.comparisonNegated;
      const isCdt = comparisonNegated === FOUND_IN_CDT ||
        comparisonNegated === NOT_FOUND_IN_CDT;

      return {
        column: column.label,
        value: uiRule.values.map((value) => {
          if (isCdt) {
            const comparisonValue = value.comparisonValue;
            const cdtOptions = this.customDataTablesService.pickListIdTypeaheadOptions;
            const foundCdt = cdtOptions.find((cdt) => {
              // eslint-disable-next-line eqeqeq
              return cdt.value == comparisonValue;
            });

            return foundCdt?.label || comparisonValue;
          } else {
            switch (column.comparison) {
              case Automation.Comparisons.TextIs:
              case Automation.Comparisons.SelectIs:
              case Automation.Comparisons.NumberIs:
                if (column.key === 'ApplicationStatus') {
                  return this.statusService.applicationStatusMap[
                    value.comparisonValue as any
                  ].translated;
                }
                if ('options' in uiRule.column) {
                  const foundOption = uiRule.column.options.find(option => option.value === value.comparisonValue);

                  if (foundOption) {
                    return foundOption.label;
                  }
                }

                return value.comparisonValue;
              case Automation.Comparisons.DateRange:
                if (value.comparisonType !== Automation.CriteriaComparisonTypes.Default) {
                  let display: string;
                  switch (value.comparisonType) {
                    case Automation.CriteriaComparisonTypes.Today:
                      display = this.i18n.translate('common:textToday', {}, 'Today');
                      break;
                    case Automation.CriteriaComparisonTypes.Yesterday:
                      display = this.i18n.translate('common:textYesterday', {}, 'Yesterday');
                      break;
                    case Automation.CriteriaComparisonTypes.Tomorrow:
                      display = this.i18n.translate('common:textTomorrow', {}, 'Tomorrow');
                      break;
                    case Automation.CriteriaComparisonTypes.LastWeek:
                      display = this.i18n.translate('common:textLastWeek', {}, 'Last week');
                      break;
                    case Automation.CriteriaComparisonTypes.ThisWeek:
                      display = this.i18n.translate('common:textThisWeek', {}, 'This week');
                      break;
                    case Automation.CriteriaComparisonTypes.LastMonth:
                      display = this.i18n.translate('common:textLastMonth', {}, 'Last month');
                      break;
                    case Automation.CriteriaComparisonTypes.ThisMonth:
                      display = this.i18n.translate('common:textThisMonth', {}, 'This month');
                      break;
                    case Automation.CriteriaComparisonTypes.LastYear:
                      display = this.i18n.translate('common:textLastYear', {}, 'Last year');
                      break;
                    case Automation.CriteriaComparisonTypes.ThisYear:
                      display = this.i18n.translate('common:textThisYear', {}, 'This year');
                      break;
                    case Automation.CriteriaComparisonTypes.Last365Days:
                      display = this.i18n.translate('common:textLast365Days', {}, 'Last 365 days');
                      break;
                    }

                    return display;
                }

                return `${this.dateService.formatDate(
                  value.comparisonGreaterThan
                )} - ${this.dateService.formatDate(
                  value.comparisonLessThan
                )}`;
              case Automation.Comparisons.NumberRange:
                return (`${value.comparisonGreaterThan || '0.00'} - ${value.comparisonLessThan || '999,999,999.99'}`);
              case Automation.Comparisons.IsUploaded:
                return !value.comparisonValue ?
                  this.i18n.translate('GLOBAL:lblIsNotUploaded', {}, 'Is not uploaded') :
                  this.i18n.translate('GLOBAL:lblIsUploaded', {}, 'Is uploaded');
              case Automation.Comparisons.IsSelected:
                return !value.comparisonValue ?
                  this.i18n.translate('GLOBAL:lblIsNotSelected', {}, 'Is not selected') :
                  this.i18n.translate('GLOBAL:lblIsSelected', {}, 'Is selected');
              case Automation.Comparisons.Majority:
                return value.comparisonValue === Automation.MajorityOptions.All ?
                  this.i18n.translate('GLOBAL:lblAll') : value.comparisonValue === Automation.MajorityOptions.Majority ?
                  this.i18n.translate('GLOBAL:lblMajority') : this.i18n.translate('GLOBAL:lblOne');
            }
          }
        }).join(', ')
      };
    });

    return rules;
  }

  mapAPIRulesToView (
    rules: AutomationAPI.AutomationRuleSetExpressionBaseModel[],
    objects: Automation.ObjectConfig[]
  ): Automation.CriteriaFormState[] {
    const groupedRules = rules.reduce((acc, rule) => {
      return {
        ...acc,
        [rule.columnName]: [
          ...(acc[rule.columnName] || []),
          rule
        ]
      };
    }, {} as SimpleStringMap<AutomationAPI.AutomationRuleSetExpressionBaseModel[]>);
    let err: Error;

    return Object.keys(groupedRules).map((columnName) => {
      const rulesForColumn = groupedRules[columnName];
      let [objectKey, columnKey] = columnName.split('.');

      if (!columnKey) {
        objects.some((obj) => {
          return obj.columns.some(searchColumn => {
            if (searchColumn.removeObjectName && searchColumn.key === objectKey) {
              objectKey = obj.key;
              columnKey = searchColumn.key;

              return true;
            }

            return false;
          });
        }, null);
      }

      const object = objects.find((obj) => {
        return obj.key === objectKey &&
          obj.columns.some((col) => col.key === columnKey);
      });
      const column: Automation.ObjectColumnConfig = object ?
        object.columns.find(({ key }) => key === columnKey) :
        null;

      const comparison = column?.comparison;

      let values: Automation.CriteriaValueFormState[];
      const baseRule = rulesForColumn[0];
      const isCdtRule = baseRule?.comparisonType === AutomationAPI.AutomationRuleComparisonTypes.InCdt ||
        baseRule?.comparisonType === AutomationAPI.AutomationRuleComparisonTypes.NotInCdt;

      switch (comparison) {
        case Automation.Comparisons.DateRange:
          // if relativeDateRule, we need to handle case here, otherwise it goes into number range logic
          const relativeDateRule = this.getRelativeDateRule(rulesForColumn);
          if (!!relativeDateRule) {
            // this is any because Typescript is comparing the values and is unaware that the values overlap since we are insuring this above
            values = this.getValuesForRelativeDateRule(baseRule, relativeDateRule);
            break;
          }
        // eslint-disable-next-line no-fallthrough
        case Automation.Comparisons.NumberRange:
          if (!isCdtRule) {
            values = this.getNumberRangeValues(rulesForColumn, comparison);
          } else {
            values = this.getCdtValuesForView(rulesForColumn, !object || !column);
          }
          break;
        case Automation.Comparisons.Majority:
          values = this.getValuesForMajority(baseRule);
          break;
        default:
          const response = this.getValuesForDefault(rulesForColumn, comparison, column, object, isCdtRule);
          err = response.err;
          values = response.values;
      }

      const hasNotDeletedVal = values.some((val) => {
        return !val.isRemoved;
      });
      if (!hasNotDeletedVal) {
        values.push({
          comparisonType: Automation.CriteriaComparisonTypes.Default,
          comparisonValue: null,
          comparisonGreaterThan: null,
          comparisonLessThan: null,
          comparisonNegated: false,
          isRemoved: false,
          greaterThanId: null,
          lessThanId: null,
          valueId: null
        });
      }

      const returnObj = {
        column,
        object,
        values,
        isRemoved: false,
        err
      };

      return returnObj;
    });
  }

  /**
   * Adapts the values array for view
   *
   * @param rules: Rules array
   * @param comparison: Comparison Type
   * @param column: Related Column
   * @param object: Related Object
   * @param isCdtComparison: is this for a CDT comparison?
   * @returns the adapted values
   */
  getValuesForDefault (
    rules: AutomationAPI.AutomationRuleSetExpressionBaseModel[],
    comparison: Automation.Comparisons,
    column: Automation.ObjectColumnConfig,
    object: Automation.ObjectConfig,
    isCdtComparison: boolean
  ) {
    let err: Error;
    const values = rules.map((rule) => {
      let value = rule.comparisonValue;
      if (isCdtComparison) {
        value = `${value}`;
      } else {
        if (
          (comparison === Automation.Comparisons.IsSelected) ||
          (comparison === Automation.Comparisons.IsUploaded)
        ) {
          try {
            value = JSON.parse(rule.comparisonValue);
          } catch (e) {
            err = e as typeof err;
            value = null;
          }
        }
        if (column && 'options' in column) {
          const foundOption = (column as Automation.ObjectColumnSelectType)
            .options.find(option => {
              // eslint-disable-next-line eqeqeq
              return option.value == value;
            });

          if (!foundOption) {
            err = new Error('missing select option');
            value = null;
          } else {
            value = foundOption.value;
          }
        }

      }

      const subFormState: Automation.CriteriaValueFormState = {
        comparisonValue: value,
        comparisonType: Automation.CriteriaComparisonTypes.Default,
        comparisonGreaterThan: null,
        comparisonLessThan: null,
        comparisonNegated: this.getComparisonNegatedFromApi(rule.comparisonType),
        isRemoved: !object || !column,
        greaterThanId: null,
        lessThanId: null,
        valueId: rule.automationRuleSetExpressionId
      };

      return subFormState;
    });

    return {
      err,
      values
    };
  }

  /**
   * Gets the Values array for type Majority
   *
   * @param baseRule: Base Rule
   * @returns the values array for majority
   */
  getValuesForMajority (
    baseRule: AutomationAPI.AutomationRuleSetExpressionBaseModel
  ): Automation.CriteriaValueFormState[] {
    const apiValue = +baseRule?.comparisonValue;
    const comparisonValue: any = apiValue === 1 ?
      Automation.MajorityOptions.All :
      apiValue === 0 ?
      Automation.MajorityOptions.One :
      Automation.MajorityOptions.Majority;

    return [{
      comparisonType: Automation.CriteriaComparisonTypes.Default,
      comparisonGreaterThan: null,
      comparisonLessThan: null,
      comparisonValue,
      isRemoved: false,
      comparisonNegated: null,
      greaterThanId: null,
      lessThanId: null,
      valueId: baseRule?.automationRuleSetExpressionId
    }];
  }

  /**
   * Gets the values array for number range type
   *
   * @param rules: Rules array
   * @param comparison: Comparison Type
   * @returns the values array for number ranges
   */
  getNumberRangeValues (
    rules: AutomationAPI.AutomationRuleSetExpressionBaseModel[],
    comparison: Automation.Comparisons
  ): Automation.CriteriaValueFormState[] {
    const comparisonGreaterThanRule = rules.find(rule =>
      [
        AutomationAPI.AutomationRuleComparisonTypes.GreaterThan,
        AutomationAPI.AutomationRuleComparisonTypes.GreaterThanOrEquals
      ].includes(rule.comparisonType));
    const comparisonLessThanRule = rules.find(rule =>
      [
        AutomationAPI.AutomationRuleComparisonTypes.LessThan,
        AutomationAPI.AutomationRuleComparisonTypes.LessThanOrEquals
      ].includes(rule.comparisonType));
    let greaterThanVal = comparisonGreaterThanRule?.comparisonValue;
    let lessThanVal = comparisonLessThanRule?.comparisonValue;
    const isDate = comparison === Automation.Comparisons.DateRange;
    if (isDate) {
      greaterThanVal = greaterThanVal ?
        this.dateService.getStartOrEndOfDayInUtcFormatted(greaterThanVal) :
        greaterThanVal;
      lessThanVal = lessThanVal ?
        this.dateService.getStartOrEndOfDayInUtcFormatted(lessThanVal) :
        lessThanVal;
    }

    return [{
      comparisonGreaterThan: greaterThanVal ?
        (isDate ? new Date(greaterThanVal) : +greaterThanVal) :
        null,
      comparisonLessThan: lessThanVal ?
        (isDate ? new Date(lessThanVal) : +lessThanVal) :
        null,
      comparisonType: Automation.CriteriaComparisonTypes.Default,
      comparisonValue: null,
      comparisonNegated: null,
      isRemoved: false,
      greaterThanId: comparisonGreaterThanRule ?
        comparisonGreaterThanRule.automationRuleSetExpressionId :
        null,
      lessThanId: comparisonLessThanRule ?
        comparisonLessThanRule.automationRuleSetExpressionId :
        null,
      valueId: null
    }];
  }

  /**
   * Finds and returns the relative date rule if exists
   *
   * @param rules: Rules array
   * @returns the found relative date rule
   */
  getRelativeDateRule (rules: AutomationAPI.AutomationRuleSetExpressionBaseModel[]) {
    return rules.find(rule =>
      [
        AutomationAPI.AutomationRuleComparisonTypes.Last365Days,
        AutomationAPI.AutomationRuleComparisonTypes.LastMonth,
        AutomationAPI.AutomationRuleComparisonTypes.LastWeek,
        AutomationAPI.AutomationRuleComparisonTypes.LastYear,
        AutomationAPI.AutomationRuleComparisonTypes.ThisMonth,
        AutomationAPI.AutomationRuleComparisonTypes.ThisWeek,
        AutomationAPI.AutomationRuleComparisonTypes.ThisYear,
        AutomationAPI.AutomationRuleComparisonTypes.Today,
        AutomationAPI.AutomationRuleComparisonTypes.Tomorrow,
        AutomationAPI.AutomationRuleComparisonTypes.Yesterday
      ].includes(rule.comparisonType));
  }

  /**
   * Gets the values for relative date rules
   *
   * @param baseRule: Base rule
   * @param relativeDateRule: Relative date rule
   * @returns the values for the relative date rule
   */
  getValuesForRelativeDateRule (
    baseRule: AutomationAPI.AutomationRuleSetExpressionBaseModel,
    relativeDateRule: AutomationAPI.AutomationRuleSetExpressionBaseModel
  ): Automation.CriteriaValueFormState[] {
    const baseComparisonType: Automation.CriteriaComparisonTypes = relativeDateRule.comparisonType as any;

    return [{
      comparisonType: baseComparisonType,
      comparisonGreaterThan: null,
      comparisonLessThan: null,
      comparisonValue: '',
      isRemoved: false,
      comparisonNegated: null,
      greaterThanId: null,
      lessThanId: null,
      valueId: baseRule?.automationRuleSetExpressionId
    }];
  }

  /**
   * Gets the values for CDT rules
   *
   * @param rules: Rules array
   * @param isRemoved: Is removed?
   * @returns the values for cdt rules
   */
  getCdtValuesForView (
    rules: AutomationAPI.AutomationRuleSetExpressionBaseModel[],
    isRemoved: boolean
  ) {
    return rules.map((rule) => {
      const subFormState: Automation.CriteriaValueFormState = {
        comparisonValue: rule.comparisonValue,
        comparisonType: Automation.CriteriaComparisonTypes.Default,
        comparisonGreaterThan: null,
        comparisonLessThan: null,
        comparisonNegated: this.getComparisonNegatedFromApi(rule.comparisonType),
        isRemoved,
        greaterThanId: null,
        lessThanId: null,
        valueId: rule.automationRuleSetExpressionId
      };

      return subFormState;
    });
  }

  /**
   * Resets the existing values (mark as removed), and creates a new blank value
   *
   * @param valuesArray: Values Array
   * @param comparisonNegated: Negated value
   */
  resetValues (
    valuesArray: UntypedFormArray,
    comparisonNegated: Automation.NegatedType = false
  ) {
    if (valuesArray) {
      for (let i = 0; i < valuesArray.length; i++) {
        valuesArray.at(i).get('isRemoved').setValue(true);
      }
      const newValue: Automation.CriteriaValueFormState = {
        comparisonType: Automation.CriteriaComparisonTypes.Default,
        comparisonValue: null,
        comparisonGreaterThan: null,
        comparisonLessThan: null,
        comparisonNegated,
        isRemoved: false,
        greaterThanId: null,
        lessThanId: null,
        valueId: null
      };
      valuesArray.push(this.formBuilder.group(newValue));
    }
  }
}
